import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * MediaLightbox — full-screen media viewer matching TimeCapsule style.
 * Renders via portal to document.body so it always covers the entire viewport.
 *
 * Props:
 *   media        – array of { id, type, url }
 *   currentIndex – index of media currently shown
 *   onIndexChange– (newIndex) => void
 *   onClose      – () => void
 */
function MediaLightbox({ media = [], currentIndex = 0, onIndexChange, onClose }) {
  const item = media[currentIndex];

  const isAudioMedia = (m) =>
    m.type === 'audio' || (m.type === 'video' && m.url?.includes('audio'));

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < media.length - 1;

  const goPrev = useCallback(() => {
    if (canGoPrev) onIndexChange(currentIndex - 1);
  }, [canGoPrev, currentIndex, onIndexChange]);

  const goNext = useCallback(() => {
    if (canGoNext) onIndexChange(currentIndex + 1);
  }, [canGoNext, currentIndex, onIndexChange]);

  // Keyboard navigation + lock body scroll
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose, goPrev, goNext]);

  if (!item) return null;

  const lightbox = (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, zIndex: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: '50%' }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
      >
        <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Previous arrow */}
      {canGoPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: '50%' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
        >
          <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Next arrow */}
      {canGoNext && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: '50%' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
        >
          <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Media content */}
      <div
        style={{ maxWidth: '90vw', maxHeight: '85vh', width: '100%', margin: '0 16px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {isAudioMedia(item) ? (
          <div style={{ background: '#1a1a2e', borderRadius: 12, padding: 32, maxWidth: 400, margin: '0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🎵</div>
            <p style={{ color: 'white', marginBottom: 16 }}>Voice Recording</p>
            <audio
              key={item.id}
              controls
              autoPlay
              src={item.url}
              style={{ width: '100%' }}
            >
              Your browser does not support the audio tag.
            </audio>
          </div>
        ) : item.type === 'video' ? (
          <video
            key={item.id}
            controls
            autoPlay
            src={item.url}
            style={{ maxWidth: '100%', maxHeight: '85vh', margin: '0 auto', display: 'block' }}
          >
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={item.url}
            alt={`Media ${currentIndex + 1}`}
            style={{ maxWidth: '100%', maxHeight: '85vh', margin: '0 auto', display: 'block', objectFit: 'contain' }}
          />
        )}
      </div>

      {/* Counter badge */}
      {media.length > 1 && (
        <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', color: 'white', fontSize: 14, background: 'rgba(0,0,0,0.5)', padding: '4px 12px', borderRadius: 20 }}>
          {currentIndex + 1} / {media.length}
        </div>
      )}
    </div>
  );

  return createPortal(lightbox, document.body);
}

export default MediaLightbox;
